import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SortingState } from '@tanstack/react-table';
import dayjs from 'utils/dayjs';
import { useAppDispatch, useAppSelector } from 'store';
import { getCompletedAction, setCompletedErrorAction } from 'store/actions/contract-processing-actions';
import {
  completedErrorSelector,
  completedItemsSelector,
  completedTotalItemsSelector,
} from 'store/selectors/contract-processing-selectors';
import { CompletedItem, WeekSelection } from '../types';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from '../constants';

const DEFAULT_SORTING: SortingState = [];

export interface UseCompletedDataReturn {
  items: CompletedItem[];
  totalItems: number;
  error: boolean;
  clearError: () => void;
  pagination: { pageIndex: number; pageSize: number };
  setPagination: (p: { pageIndex: number; pageSize: number }) => void;
  sorting: SortingState;
  setSorting: (s: SortingState) => void;
  refetch: () => void;
  selectedItems: CompletedItem[];
  currentPageSelectedRows: number[];
  isAllOnPageSelected: boolean;
  isSomeSelected: boolean;
  handleSelectAllOnPage: () => void;
  handleSelectionChange: (ids: (string | number)[]) => void;
  handleClearAll: () => void;
  deselect: (ids: number[]) => void;
}

function mapSortingToApi(sorting: SortingState): { sortField?: string; sortDirection?: 'asc' | 'desc' } {
  if (sorting.length === 0) {
    return {};
  }
  const { id, desc } = sorting[0];
  return { sortField: id, sortDirection: desc ? 'desc' : 'asc' };
}

export const useCompletedData = (
  companyId: number | null,
  weeks: WeekSelection[],
  date?: string | null,
  employeeIds?: number[],
  costCenterId?: number | null,
  locationId?: number | null,
  workerClass?: string | null,
  functionId?: number | null,
): UseCompletedDataReturn => {
  const dispatch = useAppDispatch();

  const employeeIdsKey = JSON.stringify(employeeIds ?? []);
  const weeksKey = JSON.stringify(weeks);

  const items = useAppSelector(completedItemsSelector);
  const totalItems = useAppSelector(completedTotalItemsSelector);
  const error = useAppSelector(completedErrorSelector);

  const [pagination, setPagination] = useState({ pageIndex: DEFAULT_PAGE_INDEX, pageSize: DEFAULT_PAGE_SIZE });
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);
  const [selected, setSelected] = useState<Record<number, CompletedItem>>({});

  const prevCompanyIdRef = useRef<number | null>(null);

  const clearError = useCallback(() => {
    dispatch(setCompletedErrorAction(false));
  }, [dispatch]);

  const refetch = useCallback(() => {
    setPagination(prev => ({ ...prev }));
  }, []);

  useEffect(() => {
    if (!companyId || weeksKey === '[]') return;

    const companyChanged = prevCompanyIdRef.current !== null && prevCompanyIdRef.current !== companyId;
    prevCompanyIdRef.current = companyId;

    let fetchPagination = pagination;
    let fetchSorting = sorting;

    if (companyChanged) {
      fetchPagination = { pageIndex: DEFAULT_PAGE_INDEX, pageSize: DEFAULT_PAGE_SIZE };
      fetchSorting = DEFAULT_SORTING;
      setPagination(fetchPagination);
      setSorting(fetchSorting);
      setSelected({});
    }

    const { sortField, sortDirection } = mapSortingToApi(fetchSorting);
    const formattedDate = date ? dayjs(new Date(date)).format('DD/MM/YYYY') : undefined;
    const promise = dispatch(
      getCompletedAction({
        companyId,
        filters: {
          pageIndex: fetchPagination.pageIndex + 1,
          pageSize: fetchPagination.pageSize,
          sortField,
          sortDirection,
          weeks: JSON.parse(weeksKey),
          date: formattedDate,
          employeeIds: employeeIds?.length ? employeeIds : undefined,
          costCenterId: costCenterId || undefined,
          locationId: locationId || undefined,
          workerClass: workerClass || undefined,
          functionId: functionId || undefined,
        },
      }),
    );

    // eslint-disable-next-line consistent-return
    return () => {
      promise.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyId,
    pagination,
    sorting,
    weeksKey,
    date,
    employeeIdsKey,
    costCenterId,
    locationId,
    workerClass,
    functionId,
    dispatch,
  ]);

  useEffect(() => {
    setPagination(prev => (prev.pageIndex === DEFAULT_PAGE_INDEX ? prev : { ...prev, pageIndex: DEFAULT_PAGE_INDEX }));
  }, [weeksKey, date, employeeIdsKey, costCenterId, locationId, workerClass, functionId]);

  const pageIds = useMemo(() => items.map(i => i.serviceDateId), [items]);
  const selectedItems = useMemo(() => Object.values(selected), [selected]);

  const currentPageSelectedRows = useMemo(() => pageIds.filter(id => !!selected[id]), [pageIds, selected]);

  const isAllOnPageSelected = useMemo(
    () => pageIds.length > 0 && currentPageSelectedRows.length === pageIds.length,
    [pageIds.length, currentPageSelectedRows.length],
  );

  const isSomeSelected = useMemo(
    () => currentPageSelectedRows.length > 0 && currentPageSelectedRows.length < pageIds.length,
    [currentPageSelectedRows.length, pageIds.length],
  );

  const handleClearAll = useCallback(() => {
    setSelected({});
  }, []);

  const deselect = useCallback((ids: number[]) => {
    setSelected(prev => {
      const next = { ...prev };
      ids.forEach(id => delete next[id]);
      return next;
    });
  }, []);

  const handleSelectAllOnPage = useCallback(() => {
    setSelected(prev => {
      const next = { ...prev };
      if (isAllOnPageSelected) {
        pageIds.forEach(id => delete next[id]);
      } else {
        items.forEach(item => {
          next[item.serviceDateId] = item;
        });
      }
      return next;
    });
  }, [isAllOnPageSelected, pageIds, items]);

  const handleSelectionChange = useCallback(
    (ids: (string | number)[]) => {
      setSelected(prev => {
        const next = { ...prev };
        items.forEach(item => {
          if (ids.includes(item.serviceDateId)) {
            next[item.serviceDateId] = item;
          } else {
            delete next[item.serviceDateId];
          }
        });
        return next;
      });
    },
    [items],
  );

  return {
    items,
    totalItems,
    error,
    clearError,
    pagination,
    setPagination,
    sorting,
    setSorting,
    refetch,
    selectedItems,
    currentPageSelectedRows,
    isAllOnPageSelected,
    isSomeSelected,
    handleSelectAllOnPage,
    handleSelectionChange,
    handleClearAll,
    deselect,
  };
};
